import { Injectable } from '@angular/core';
import { firstValueFrom, map, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { gtag } from '@/shared/gtag';
import { Category } from '@/types/bedek';
import { DEFECTS_COUNTER, PLACES_COUNTER } from '@/services/api-count';
import { ensureReportMedia, type ReportMediaFailure } from './ensure-report-media';
import type { MediaUploadContext } from '@mtng-reports/models';
import type { ReportPublicationTelemetry } from './report-publication-telemetry.service';

const POST_TYPE = 'reports';

@Injectable({
  providedIn: 'root',
})
export class BedekApiService {
  constructor(
    private apiService: ApiService,
  ) {}

  /** Uploads any gallery image still missing stored media and writes the reference back. */
  ensureReportMedia(dataArr: any[], mediaUploadContext: MediaUploadContext): Promise<ReportMediaFailure | null> {
    return ensureReportMedia(dataArr, mediaUploadContext, {
      checkConnection: () => this.apiService.checkConnection(),
      upload: (dataUrl, context) => firstValueFrom(this.apiService.media(dataUrl, context)),
    });
  }

  async createReport(obj, publicationTelemetry?: ReportPublicationTelemetry) {
    gtag('event', 'create_report', {
      type: 'bedek',
    });
    return this.apiService.createReport(POST_TYPE, obj, publicationTelemetry);
  }

  loadPlaces() {
    return this.apiService.loadPlaces(PLACES_COUNTER);
  }

  loadPlaceSuggestionStats() {
    return this.apiService.getPlaceSuggestionStats();
  }

  loadCategories(): Observable<Category[]> {
    return this.apiService.loadCategories('bedek').pipe(
      map((categories) => categories.map(({ id, title }) => ({ id, title }) as Category)),
    );
  }

  loadDefects() {
    return this.apiService.loadDefects('bedek');
  }

  loadDefectsWithCounter() {
    return this.loadDefects().pipe(
      map((defects) =>
        defects
          .map((defect) => ({ ...defect, counter: DEFECTS_COUNTER[defect.ID] || 0 }))
          .sort((a, b) => b.counter - a.counter)
      )
    );
  }
}
